import React, { useCallback } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';
import { FiCpu, FiShield, FiCode, FiTerminal } from 'react-icons/fi';
import ScrollReveal from './ScrollReveal';
import styles from './Skills.module.css';

function SkillCard({ category, delay }) {
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springX = useSpring(rotateX, { stiffness: 200, damping: 25 });
  const springY = useSpring(rotateY, { stiffness: 200, damping: 25 });

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    rotateX.set((y - 0.5) * -10);
    rotateY.set((x - 0.5) * 10);
  }, [rotateX, rotateY]);

  const handleMouseLeave = useCallback(() => {
    rotateX.set(0);
    rotateY.set(0);
  }, [rotateX, rotateY]);

  return (
    <ScrollReveal direction="up" distance={40} duration={0.7} delay={delay}>
      <motion.div
        className={styles.card}
        style={{
          rotateX: springX,
          rotateY: springY,
          transformPerspective: 900,
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        whileHover={{ scale: 1.02 }}
        transition={{ scale: { type: 'spring', stiffness: 250, damping: 25 } }}
      >
        <div className={styles.header}>
          <div className={styles.meta}>
            <span className={styles.icon}>{category.icon}</span>
            <span className={styles.categoryName}>{category.category}</span>
          </div>
          <div className={styles.statusBadge}>
            <span className={styles.statusDot} />
            {category.status}
          </div>
        </div>

        <h3 className={styles.title}>{category.title}</h3>
        <p className={styles.description}>{category.description}</p>

        <div className={styles.skillsList}>
          {category.skills.map((skill, sIdx) => (
            <div key={sIdx} className={styles.skillRow}>
              <span className={styles.skillName}>{skill.name}</span>
              <span className={styles.skillLevelBadge}>{skill.type}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </ScrollReveal>
  );
}

function Skills() {
  const skillModules = [
    {
      id: 'embedded',
      icon: <FiCpu />,
      category: 'Hardware & Firmware',
      status: 'OPERATIONAL',
      title: 'Embedded Systems',
      description: 'Developing and analyzing secure embedded firmware and bare-metal architectures.',
      skills: [
        { name: 'ESP32 / ESP8266', type: 'Intermediate' },
        { name: 'ARM Cortex-M', type: 'Intermediate' },
        { name: 'Arduino / Teensy', type: 'Intermediate' },
        { name: 'Firmware Extraction', type: 'Security' },
      ],
    },
    {
      id: 'security',
      icon: <FiShield />,
      category: 'Vulnerability Analysis',
      status: 'ACTIVE VECTORS',
      title: 'Offensive Security',
      description: 'Conducting network, wireless protocol exploitation, and hardware reverse engineering.',
      skills: [
        { name: 'Wireless Protocols', type: 'Intermediate' },
        { name: 'Hardware Hacking', type: 'Intermediate' },
        { name: 'Firmware Reversing', type: 'Intermediate' },
        { name: 'Vulnerability Analysis', type: 'Intermediate' },
      ],
    },
    {
      id: 'programming',
      icon: <FiCode />,
      category: 'Compiler & Scripting',
      status: 'NATIVE',
      title: 'Languages',
      description: 'Writing low-level code, scripts, and automation tools.',
      skills: [
        { name: 'C / C++', type: 'Intermediate' },
        { name: 'Python', type: 'Intermediate' },
        { name: 'Bash Scripting', type: 'Intermediate' },
        { name: 'Assembly (ARM/x86)', type: 'Familiar' },
      ],
    },
    {
      id: 'tools',
      icon: <FiTerminal />,
      category: 'Security Infrastructure',
      status: 'OPERATIONAL',
      title: 'Tools & Platforms',
      description: 'Expert usage of security tools and environments.',
      skills: [
        { name: 'Kali Linux', type: 'Intermediate' },
        { name: 'Wireshark', type: 'Intermediate' },
        { name: 'Ghidra / Binwalk', type: 'Intermediate' },
        { name: 'Burp Suite / Nmap', type: 'Intermediate' },
      ],
    },
  ];

  return (
    <section id="skills" className={styles.skills}>
      <div className="container">
        <ScrollReveal direction="up" distance={40} duration={0.6}>
          <div className="section-header">
            <p className="label">Expertise</p>
            <h2>Technical Skills</h2>
          </div>
        </ScrollReveal>

        <div className={styles.grid}>
          {skillModules.map((category, idx) => (
            <SkillCard
              key={category.id}
              category={category}
              delay={idx * 0.1}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Skills;
